import { Link } from "react-router-dom";
import { Sparkles, Heart, Users, Trees, Library, ShoppingBag, Mail, HandHeart, Smile } from "lucide-react";

const HUB_IMG =
    "https://images.unsplash.com/photo-1747738609473-9e92707075eb?fm=jpg&q=70&w=1200&fit=crop&auto=format";
const HUB_IMG_FALLBACK =
    "https://images.unsplash.com/photo-1480561807109-e2aa33f23be8?fm=jpg&q=70&w=1200&fit=crop&auto=format";

const ideas = [
    { icon: Trees, color: "bg-mint", title: "Care for creation", desc: "Pick up litter at the local park, plant flowers, or start a tiny garden for neighbors to enjoy." },
    { icon: Library, color: "bg-sun", title: "Be a library hero", desc: "Donate gently-used books, read aloud to a younger sibling, or organize a kids' book swap." },
    { icon: ShoppingBag, color: "bg-coral", title: "Feed the hungry", desc: "Pack a food drive box with your family for a local food pantry. Every can counts!" },
    { icon: Mail, color: "bg-sky", title: "Send kindness mail", desc: "Make handmade cards for elderly neighbors, hospital patients, or your local fire station." },
    { icon: HandHeart, color: "bg-lilac", title: "Help a neighbor", desc: "Rake leaves, walk a dog, or take groceries to someone who's tired or sick. Small things = big love." },
    { icon: Smile, color: "bg-mint", title: "Welcome the new kid", desc: "Sit with someone new at lunch. Invite a classmate to play. Loneliness is the easiest thing to fix." },
];

export default function Community() {
    return (
        <div data-testid="community-page">
            <section className="border-b-2 border-ink bg-mint/30">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <span className="pill bg-white">Community Action Hub</span>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 leading-[1.05]">
                            Faith looks like
                            <span className="block bg-coral text-white px-3 mt-2 inline-block border-2 border-ink rounded-2xl shadow-sticker-sm rotate-[-2deg]">
                                going the 2nd mile.
                            </span>
                        </h1>
                        <p className="mt-5 text-lg text-ink/80 max-w-xl">
                            "If anyone forces you to go one mile, go with him two miles." — <span className="font-semibold">Matthew 5:41 (ESV)</span>. Every Bible lesson on 2nd Mile Kids ends with real, Christ-like actions kids can take this week — at school, in the neighborhood, at church, in the park.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Link to="/lessons" className="btn-sticker bg-sun text-ink" data-testid="hub-cta-lessons">
                                <Sparkles className="w-5 h-5 mr-2" strokeWidth={2.5} /> Browse lessons
                            </Link>
                        </div>
                    </div>
                    <div className="relative">
                        <div className="rounded-[2rem] border-2 border-ink overflow-hidden shadow-sticker-lg">
                            <img
                                src={HUB_IMG}
                                onError={(e) => { if (e.target.src !== HUB_IMG_FALLBACK) e.target.src = HUB_IMG_FALLBACK; }}
                                alt="An open Holy Bible"
                                className="w-full h-[380px] object-cover"
                            />
                        </div>
                        <div className="absolute -bottom-5 -left-5 px-5 py-3 bg-white border-2 border-ink rounded-2xl shadow-sticker font-accent text-2xl text-coral">
                            love your neighbor!
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <span className="pill bg-sun mb-3">Action ideas</span>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
                        Six everyday ways to serve
                    </h2>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {ideas.map((idea, i) => (
                        <div key={i} className="card-sticker p-6 bg-white hover:shadow-sticker-lg hover:-translate-x-[2px] hover:-translate-y-[2px] transition-all" data-testid={`idea-${i}`}>
                            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl border-2 border-ink ${idea.color} mb-4`}>
                                <idea.icon className="w-7 h-7 text-ink" strokeWidth={2.5} />
                            </div>
                            <h3 className="font-heading text-xl font-bold mb-2">{idea.title}</h3>
                            <p className="text-ink/70 leading-relaxed">{idea.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20">
                <div className="card-sticker bg-sky/40 p-8 sm:p-12">
                    <div className="flex items-start gap-4">
                        <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-coral border-2 border-ink shrink-0">
                            <Heart className="w-6 h-6 text-white" strokeWidth={2.5} />
                        </span>
                        <div>
                            <h3 className="font-heading text-2xl font-bold">A note for grown-ups</h3>
                            <p className="text-ink/80 leading-relaxed mt-2">
                                These are starting points — every kid and church family is different. Use what fits, skip what doesn't,
                                and always make sure activities are safe and age-appropriate. The goal isn't to do something big.
                                It's to teach kids that the gospel of Jesus Christ always shows up in love for real people (1 John 3:18).
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
